import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  merge,
  NEVER,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  tap,
  distinctUntilChanged,
  pairwise,
  startWith,
  withLatestFrom,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ElementActions,
  ElementTypes,
  GaContext,
  MediaQueryService,
  OfferListFetchParams,
  isShortHaulFlight,
} from '@fcom/common';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { Cabin } from '@fcom/dapi/api/models';
import { getLocation } from '@fcom/locations';
import {
  ButtonSize,
  ButtonTheme,
  ButtonType,
  CheckBoxTheme,
  IconPosition,
  RadioItemGroupOption,
  TabTheme,
  PopoverOptions,
  PopoverPosition,
  PopoverService,
} from '@fcom/ui-components';
import { unsubscribe } from '@fcom/core/utils';
import { TicketSelectionPhase } from '@fcom/voluntary-change/enums';
import { AirOffersStatus, BookingAppState, SelectionPhase } from '@fcom/common/interfaces/booking';
import { BookingAirBoundsService } from '@fcom/booking/modules/ticket-selection/services';
import { loginStatus, profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { isPresent, SentryLogger } from '@fcom/core';
import {
  resetAllNextFilters,
  setAllNextFilters,
  setNextArrivalTime,
  setNextDepartureTime,
} from '@fcom/common/store/actions/bounds.actions';
import { LoginStatus } from '@fcom/core-api/login';
import { finShare } from '@fcom/rx';

import { defaultFilterValues, FlightFilters, FlightTimeOption } from '../../interfaces/flight-filters.interface';

export interface ResetFiltersEvent {
  type: keyof FlightFilters;
  cabin?: Cabin;
}

export interface ResetAllFiltersEvent {
  defaultCabin: Cabin | undefined;
}

@Component({
  selector: 'fin-flight-selection-filters',
  styleUrls: ['./flight-selection-filters.component.scss'],
  templateUrl: './flight-selection-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlightSelectionFiltersComponent implements OnInit, OnChanges, OnDestroy {
  private store$ = inject<Store<BookingAppState>>(Store);
  private popoverService = inject(PopoverService);
  private mediaQueryService = inject(MediaQueryService);
  private gtmService = inject(GtmService);
  private bookingAirBoundsService = inject(BookingAirBoundsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private fb = inject(UntypedFormBuilder);
  private sentryLogger = inject(SentryLogger);

  /**
   * Whether radio buttons in travel class filter should be disabled
   */
  @Input()
  isTravelClassChoicesDisabled: boolean;

  @Input()
  selectionPhase: SelectionPhase | TicketSelectionPhase;

  @Input()
  showRefundability = true;

  @Input()
  travelClasses$: Observable<Cabin[]>;

  /**
   * How many results the current filters produce
   * In desktop version used in all dropdowns' footer buttons
   * In mobile used in modal's footer button
   */
  @Input()
  resultsAmount: number;

  /**
   * Amount of layovers each flight has.
   * Used in stops filter
   */
  @Input()
  layoverAmounts: number[] = [];

  /**
   * Used in times filter
   */
  @Input()
  originLocationCode: string;

  /**
   * Used in times filter
   */
  @Input()
  destinationLocationCode: string;

  @Input() filters$: Observable<FlightFilters>;

  @Input()
  cabinLastRequestParam$: Observable<{ cabin: Cabin }> = of({ cabin: Cabin.MIXED });

  @Input()
  isTravelClassDisabled = false;

  @Input()
  showFlightSelectionFilters: boolean;

  @Input()
  isNewFilterStyle = false;

  @Input()
  boundsHasDirectFlights$: Observable<boolean> = NEVER;

  @Input()
  allBoundsAreDirect$: Observable<boolean> = NEVER;

  @Input()
  amountOfDirectFlights$: Observable<number> = NEVER;

  @Output()
  setFilter: EventEmitter<FlightFilters> = new EventEmitter();
  @Output()
  resetFilter: EventEmitter<ResetFiltersEvent> = new EventEmitter();
  @Output()
  resetAllFilters: EventEmitter<ResetAllFiltersEvent> = new EventEmitter();
  @Output()
  showSpinner: EventEmitter<void> = new EventEmitter();

  selectedStopsOption: number = defaultFilterValues.defaultStopsOption;
  selectedMaxStopsOption: number = defaultFilterValues.defaultStopsOption;
  selectedRefundabilityOption: boolean = defaultFilterValues.defaultRefundabilityOption;
  selectedTravelClass: Cabin;
  selectedDepartureTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  selectedArrivalTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  selectedNextDepartureTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  selectedNextArrivalTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  defaultInboundTravelClass: Cabin;

  departureCityName: string;
  arrivalCityName: string;

  isModalOpen: boolean;

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly CheckBoxTheme = CheckBoxTheme;
  readonly ButtonSize = ButtonSize;
  readonly IconPosition = IconPosition;
  readonly TabTheme = TabTheme;
  readonly FlightTimeOption = FlightTimeOption;
  readonly Cabin = Cabin;
  readonly AirOffersStatus = AirOffersStatus;
  readonly SelectionPhase = SelectionPhase;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TicketSelectionPhase = TicketSelectionPhase;

  popoverOptions: PopoverOptions = {
    popoverID: 'flight-selection-filters',
    openByDefault: false,
    closeOnScroll: false,
    showArrowCaret: false,
    showLeftBorder: false,
    popoverPosition: PopoverPosition.BOTTOM,
    roundedCorners: true,
  };

  departureTimesForm: FormGroup<Record<string, FormControl<boolean>>>;
  arrivalTimesForm: FormGroup<Record<string, FormControl<boolean>>>;
  nextDepartureTimesForm: FormGroup<Record<string, FormControl<boolean>>>;
  nextArrivalTimesForm: FormGroup<Record<string, FormControl<boolean>>>;
  directFlightsForm = new FormGroup<{ shortcut: FormControl<boolean> }>({
    shortcut: new FormControl<boolean>(false),
  });

  activeTab$ = new BehaviorSubject(0);
  showStopsFilter$: Observable<boolean>;
  awardQueryParams$: Observable<OfferListFetchParams>;
  isMobile$: Observable<boolean>;
  showClearAllBtn: boolean;
  shouldNotClearTravelClass: boolean;
  defaultTravelClass: Cabin;
  selectedFiltersLength$: BehaviorSubject<number> = new BehaviorSubject(0);
  resizedWatcher$: Subject<void> = new Subject();
  subscriptions: Subscription = new Subscription();
  filtersForm: FormGroup<{
    stopsCount: FormControl<null | string>;
    refundable: FormControl<string>;
    cabin: FormControl<Cabin>;
  }>;
  travelClassFilters: RadioItemGroupOption[] = [];
  updateFilters$: Subject<void> = new Subject();
  loginStatus$: Observable<LoginStatus>;

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());

    this.subscriptions.add(
      this.cabinLastRequestParam$
        ?.pipe(
          filter(Boolean),
          withLatestFrom(this.store$.pipe(profileOrUndefinedIfNotLoggedIn()), this.isLongHaul()),
          // Don't do initial filtering for corporate long-hauls
          filter(([_, profile, isLongHaul]) => !(profile?.isCorporate && isLongHaul)),
          take(1),
          tap(([params]) => {
            if (this.selectionPhase === SelectionPhase.OUTBOUND) {
              this.setFilter.emit({ cabin: params.cabin });
            }
          })
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.travelClasses$
        .pipe(
          tap((travelClasses) => {
            const defaultIsNotMixedCabinForInbound =
              this.selectionPhase === SelectionPhase.INBOUND && this.defaultInboundTravelClass !== Cabin.MIXED;
            this.shouldNotClearTravelClass =
              this.isTravelClassDisabled || defaultIsNotMixedCabinForInbound || travelClasses.length === 1;
          }),
          take(1)
        )
        .subscribe()
    );
    this.subscriptions.add(
      combineLatest([this.travelClasses$, this.cabinLastRequestParam$])
        .pipe(take(1))
        .subscribe(([travelClasses, params]) => {
          this.defaultTravelClass = travelClasses.includes(Cabin.MIXED) ? Cabin.MIXED : params.cabin;
        })
    );

    this.subscriptions.add(
      this.travelClasses$.pipe(take(1)).subscribe((cabinArray) => {
        cabinArray.forEach((cabin) => {
          this.travelClassFilters.push({
            value: cabin,
            label: 'travelClass.' + cabin.toLowerCase(),
          });
        });
      })
    );

    this.subscriptions.add(this.filters$.pipe(tap(this.handleFilterChange.bind(this))).subscribe());

    this.showStopsFilter$ = this.shouldShowStopsFilter();
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.updateLocations();

    this.filtersForm = this.fb.group({
      stopsCount: this.fb.control(null),
      cabin: this.fb.control({ value: this.defaultTravelClass, disabled: this.isTravelClassChoicesDisabled }),
      refundable: this.fb.control(false),
    });

    // fcom-checkbox-input requires a FormGroup + controlName binding rather than the simple [checked]/(change)
    // pattern of fcom-checkbox, so each time-filter group needs its own reactive form with subscriptions to
    // keep the form values in sync with the NgRx store state.
    this.departureTimesForm = this.buildTimesFormGroup(this.selectedDepartureTimes);
    this.arrivalTimesForm = this.buildTimesFormGroup(this.selectedArrivalTimes);
    this.nextDepartureTimesForm = this.buildTimesFormGroup(this.selectedNextDepartureTimes);
    this.nextArrivalTimesForm = this.buildTimesFormGroup(this.selectedNextArrivalTimes);

    this.subscriptions.add(
      combineLatest([
        this.filtersForm.get('stopsCount').valueChanges.pipe(startWith(this.filtersForm.get('stopsCount').value)),
        this.allBoundsAreDirect$.pipe(startWith(false)),
      ]).subscribe(([stopsCount, allDirect]) => {
        this.directFlightsForm.get('shortcut').setValue(stopsCount === '0' || allDirect, { emitEvent: false });
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.boundsHasDirectFlights$.pipe(startWith(true)),
        this.allBoundsAreDirect$.pipe(startWith(false)),
      ]).subscribe(([hasDirectFlights, allDirect]) => {
        if (!hasDirectFlights || allDirect) {
          this.directFlightsForm.get('shortcut').disable({ emitEvent: false });
        } else {
          this.directFlightsForm.get('shortcut').enable({ emitEvent: false });
        }
      })
    );

    this.subscriptions.add(
      this.directFlightsForm.get('shortcut').valueChanges.subscribe((checked) => {
        this.onToggleDirectFlights(checked);
      })
    );

    this.subscriptions.add(
      this.subscribeToTimesForm(this.departureTimesForm, (v) => this.onSelectDepartureTime(v, false))
    );
    this.subscriptions.add(this.subscribeToTimesForm(this.arrivalTimesForm, (v) => this.onSelectArrivalTime(v, false)));
    this.subscriptions.add(
      this.subscribeToTimesForm(this.nextDepartureTimesForm, (v) => this.onSelectDepartureTime(v, true))
    );
    this.subscriptions.add(
      this.subscribeToTimesForm(this.nextArrivalTimesForm, (v) => this.onSelectArrivalTime(v, true))
    );

    this.subscriptions.add(
      this.filters$
        .pipe(
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          withLatestFrom(this.allBoundsAreDirect$)
        )
        .subscribe(([filters, allBoundsAreDirect]) => {
          this.onUpdateFilters(filters, this.isNewFilterStyle, allBoundsAreDirect);
        })
    );

    this.subscriptions.add(
      this.updateFilters$
        .pipe(withLatestFrom(this.filters$, this.allBoundsAreDirect$))
        .subscribe(([_, filters, allBoundsAreDirect]) => {
          this.selectedNextArrivalTimes = this.selectedArrivalTimes;
          this.selectedNextDepartureTimes = this.selectedDepartureTimes;
          this.syncTimesForm(this.nextDepartureTimesForm, this.selectedNextDepartureTimes);
          this.syncTimesForm(this.nextArrivalTimesForm, this.selectedNextArrivalTimes);
          this.onUpdateFilters(filters, this.isNewFilterStyle, allBoundsAreDirect);
        })
    );

    if (this.isNewFilterStyle) {
      this.updateNextFiltersFromFormChanges();
    } else {
      this.updateStoreFromFormChanges();
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  ngOnChanges(): void {
    this.updateLocations();
  }

  onUpdateFilters(filters: FlightFilters, isNewFilterStyle: boolean, allBoundsAreDirect: boolean): void {
    const refundable = filters.refundable === true ? 'true' : 'false';
    const stopsCountOrMaxStops = isNewFilterStyle ? filters.maxStops : filters.stopsCount;
    const stopsCount =
      stopsCountOrMaxStops === null || stopsCountOrMaxStops === undefined ? null : String(stopsCountOrMaxStops);
    const newFiltersValue = {
      stopsCount: isNewFilterStyle && allBoundsAreDirect ? '0' : stopsCount,
      cabin: filters.cabin,
      refundable: refundable,
    };
    this.filtersForm.setValue(newFiltersValue);
    this.setNextFilters(newFiltersValue);
  }

  handleFilterChange(filters: FlightFilters): void {
    if (
      this.selectionPhase === SelectionPhase.OUTBOUND &&
      this.selectedTravelClass &&
      filters.cabin !== this.selectedTravelClass
    ) {
      this.subscriptions.add(
        this.activatedRoute.queryParams.pipe(take(1)).subscribe((p) => {
          try {
            const changedJson = JSON.stringify({
              ...JSON.parse(p.json),
              cabin: filters.cabin,
            });
            const navigationExtras: NavigationExtras = {
              queryParams: {
                json: changedJson,
              },
              queryParamsHandling: 'merge',
            };
            this.router.navigate([], navigationExtras);
          } catch (error) {
            this.sentryLogger.warn('Failed to parse deeplink', { json: p.json, error });
          }
        })
      );
    }

    if (
      this.selectedTravelClass &&
      this.selectedTravelClass !== filters.cabin &&
      this.selectionPhase === SelectionPhase.OUTBOUND
    ) {
      this.refetchBounds(filters.cabin);
    }

    this.selectedStopsOption = filters.stopsCount;
    this.selectedMaxStopsOption = filters.maxStops;
    this.selectedRefundabilityOption = filters.refundable;

    this.selectedTravelClass = filters.cabin;
    this.selectedDepartureTimes = filters.departureTime;
    this.selectedArrivalTimes = filters.arrivalTime;
    this.syncTimesForm(this.departureTimesForm, filters.departureTime);
    this.syncTimesForm(this.arrivalTimesForm, filters.arrivalTime);

    if (this.selectionPhase === SelectionPhase.INBOUND && !this.defaultInboundTravelClass) {
      this.defaultInboundTravelClass = filters.cabin;
    }

    this.updateSelectedFiltersLength();
    this.updateClearAllButton();
  }

  updateStoreFromFormChanges(): void {
    this.subscriptions.add(
      this.filtersForm.valueChanges
        .pipe(
          startWith(this.filtersForm.getRawValue()),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          pairwise()
        )
        .subscribe(([prev, next]) => {
          this.showSpinner.emit();
          this.updateCarouselWidth();

          if (prev.stopsCount !== next.stopsCount) {
            const stopsCount = next.stopsCount === null ? null : Number(next.stopsCount);
            this.setFilter.emit({ stopsCount });
            this.sendFiltersGaEvent(`booking-flow-select-filter-stops-${next.stopsCount}`);
          }

          if (prev.cabin !== next.cabin) {
            this.setFilter.emit({ cabin: next.cabin });
            this.sendFiltersGaEvent(`booking-flow-select-filter-class-${next.cabin.toLowerCase()}`);
          }

          if (prev.refundable !== next.refundable) {
            const refundable = next.refundable === 'true' ? true : false;
            this.setFilter.emit({ refundable });
            this.sendFiltersGaEvent(
              `booking-flow-select-filter-refundability-${refundable ? 'refundable' : 'nonrefundable'}`
            );
          }
        })
    );
  }

  updateNextFiltersFromFormChanges(): void {
    this.subscriptions.add(
      this.filtersForm.valueChanges
        .pipe(
          startWith(this.filtersForm.getRawValue()),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        )
        .subscribe((next) => {
          this.setNextFilters(next);
        })
    );
  }

  setNextFilters(formValues: Partial<{ stopsCount: string; refundable: string; cabin: Cabin }>): void {
    const nextFilters = {
      maxStops:
        formValues.stopsCount === null || formValues.stopsCount === undefined ? null : Number(formValues.stopsCount),
      cabin: formValues.cabin,
      refundable: formValues.refundable === 'true' ? true : false,
    };

    this.store$.dispatch(
      setAllNextFilters({
        nextFilters: {
          ...nextFilters,
          departureTime: this.selectedNextDepartureTimes,
          arrivalTime: this.selectedNextArrivalTimes,
        },
      })
    );
  }

  sendFiltersGaEvent(elementName: string): void {
    this.gtmService.trackElement(
      elementName,
      GaContext.BOOKING_FLOW,
      ElementTypes.RADIO,
      undefined,
      ElementActions.CLICK
    );
  }

  updateClearAllButton(): void {
    // Check if selected stops option is not the default
    const isNotDefaultStopsOption = this.selectedStopsOption !== defaultFilterValues.defaultStopsOption;

    // Check if selected refundability option is not the default
    const isNotDefaultRefundabilityOption =
      this.selectedRefundabilityOption &&
      this.selectedRefundabilityOption !== defaultFilterValues.defaultRefundabilityOption;

    // Check if selected travel class is not the default and the flag allows clearing
    const isNotDefaultTravelClass =
      this.selectedTravelClass !== defaultFilterValues.defaultTravelClass && this.shouldNotClearTravelClass === false;

    // Check if selected departure times or arrival times are less than 3
    const isLessThanThreeDepartureTimes = this.selectedDepartureTimes?.length < 3;
    const isLessThanThreeArrivalTimes = this.selectedArrivalTimes?.length < 3;

    // Set the clear button visibility based on the conditions
    this.showClearAllBtn =
      isNotDefaultStopsOption ||
      isNotDefaultRefundabilityOption ||
      isNotDefaultTravelClass ||
      isLessThanThreeDepartureTimes ||
      isLessThanThreeArrivalTimes;
  }

  updateSelectedFiltersLength(): void {
    this.subscriptions.add(
      this.allBoundsAreDirect$.pipe(take(1)).subscribe((allBoundsAreDirect) => {
        const selectedFilters = [
          this.selectedStopsOption !== defaultFilterValues.defaultStopsOption,
          this.selectedMaxStopsOption !== defaultFilterValues.defaultStopsOption && !allBoundsAreDirect,
          this.selectedRefundabilityOption !== defaultFilterValues.defaultRefundabilityOption &&
            this.selectedRefundabilityOption !== undefined,
          this.selectedTravelClass !== defaultFilterValues.defaultTravelClass && this.selectedTravelClass !== undefined,
          (this.selectedDepartureTimes?.length ?? 0) < defaultFilterValues.defaultTimeOptions.length,
          (this.selectedArrivalTimes?.length ?? 0) < defaultFilterValues.defaultTimeOptions.length,
        ].filter(Boolean).length;

        this.selectedFiltersLength$.next(selectedFilters);
      })
    );
  }

  private isLongHaul(): Observable<boolean> {
    return combineLatest([
      this.store$.select(getLocation(this.originLocationCode)),
      this.store$.select(getLocation(this.destinationLocationCode)),
    ]).pipe(
      take(1),
      map(([origin, destination]) => !isShortHaulFlight(origin, destination))
    );
  }

  private refetchBounds(cabin: Cabin) {
    const queryParams = this.bookingAirBoundsService.getLastRequestParams();

    if (this.selectionPhase === SelectionPhase.OUTBOUND && !queryParams?.isAward) {
      this.subscriptions.add(
        this.loginStatus$.pipe(take(1)).subscribe((status) => {
          this.bookingAirBoundsService.triggerFetchBounds({ ...queryParams, cabin }, status);
        })
      );
    }
  }

  clearFilter(flightFilter: keyof FlightFilters): void {
    this.showSpinner.emit();
    this.updateCarouselWidth();
    this.resetFilter.emit({
      type: flightFilter,
      cabin: this.defaultTravelClass,
    });
  }

  clearAllFilters(contextForAnalytics: 'desktop' | 'mobile' | 'empty-state' | 'master-reset'): void {
    this.showSpinner.emit();

    this.resetAllFilters.emit({
      defaultCabin: this.defaultTravelClass,
    });

    this.store$.dispatch(resetAllNextFilters());

    this.subscriptions.add(
      this.allBoundsAreDirect$
        .pipe(
          take(1),
          filter((value) => value === false)
        )
        .subscribe(() => {
          this.filtersForm.get('stopsCount').setValue(null);
        })
    );
    this.filtersForm.get('cabin').setValue(this.defaultTravelClass, { emitEvent: false });
    this.updateCarouselWidth();

    this.selectedNextDepartureTimes = defaultFilterValues.defaultTimeOptions;
    this.selectedNextArrivalTimes = defaultFilterValues.defaultTimeOptions;
    this.syncTimesForm(this.nextDepartureTimesForm, this.selectedNextDepartureTimes);
    this.syncTimesForm(this.nextArrivalTimesForm, this.selectedNextArrivalTimes);

    this.gtmService.trackElement(
      `booking-flow-clear-all-filters-${contextForAnalytics}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }

  onSelectDepartureTime(departureTime: FlightTimeOption, isTestVariant: boolean): void {
    if (isTestVariant) {
      this.selectedNextDepartureTimes = this.selectedNextDepartureTimes?.includes(departureTime)
        ? this.selectedNextDepartureTimes?.filter((d) => d !== departureTime)
        : [...this.selectedNextDepartureTimes, departureTime];
      this.store$.dispatch(setNextDepartureTime({ departureTime: this.selectedNextDepartureTimes }));
      this.syncTimesForm(this.nextDepartureTimesForm, this.selectedNextDepartureTimes);
    } else {
      const newDepartureTime = this.selectedDepartureTimes?.includes(departureTime)
        ? this.selectedDepartureTimes?.filter((d) => d !== departureTime)
        : [...this.selectedDepartureTimes, departureTime];

      this.showSpinner.emit();
      this.setFilter.emit({ departureTime: newDepartureTime });
      this.updateCarouselWidth();
    }

    this.gtmService.trackElement(
      `booking-flow-select-filter-departure-${departureTime.toLowerCase()}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.CHECKBOX,
      undefined,
      ElementActions.CLICK
    );
  }

  onSelectArrivalTime(arrivalTime: FlightTimeOption, isTestVariant: boolean): void {
    if (isTestVariant) {
      this.selectedNextArrivalTimes = this.selectedNextArrivalTimes?.includes(arrivalTime)
        ? this.selectedNextArrivalTimes.filter((d) => d !== arrivalTime)
        : [...this.selectedNextArrivalTimes, arrivalTime];
      this.store$.dispatch(setNextArrivalTime({ arrivalTime: this.selectedNextArrivalTimes }));
      this.syncTimesForm(this.nextArrivalTimesForm, this.selectedNextArrivalTimes);
    } else {
      const newArrivalTime = this.selectedArrivalTimes?.includes(arrivalTime)
        ? this.selectedArrivalTimes.filter((d) => d !== arrivalTime)
        : [...this.selectedArrivalTimes, arrivalTime];

      this.showSpinner.emit();
      this.setFilter.emit({ arrivalTime: newArrivalTime });
      this.updateCarouselWidth();
    }

    this.gtmService.trackElement(
      `booking-flow-select-filter-arrival-${arrivalTime.toLowerCase()}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.CHECKBOX,
      undefined,
      ElementActions.CLICK
    );
  }

  shouldShowStopsFilter(): Observable<boolean> {
    return this.filters$.pipe(
      take(1),
      map((filters) => {
        if (typeof filters?.stopsCount === 'number') {
          return true;
        }

        const hasDirectFlights = this.layoverAmounts.some((value) => value === 0);
        const hasMultipleStops = this.layoverAmounts.some((value) => value > 0);

        return hasDirectFlights && hasMultipleStops;
      })
    );
  }

  updateLocations(): void {
    this.subscriptions.add(
      combineLatest([
        this.store$.select(getLocation(this.originLocationCode)),
        this.store$.select(getLocation(this.destinationLocationCode)),
      ])
        .pipe(take(1))
        .subscribe(([origin, destination]) => {
          this.departureCityName = origin?.cityName;
          this.arrivalCityName = destination?.cityName;
        })
    );
  }

  updateCarouselWidth(): void {
    this.resizedWatcher$.next();
  }

  closePopover(): void {
    this.popoverService.close(true);
  }

  keepOrder(_a: unknown, _b: unknown): number {
    return 0;
  }

  getTimeRange(option: FlightTimeOption): string {
    switch (option) {
      case FlightTimeOption.MORNING:
        return '00:00 - 11:59 h';
      case FlightTimeOption.AFTERNOON:
        return '12:00 - 16:59 h';
      case FlightTimeOption.EVENING:
        return '17:00 - 23:59 h';
      default:
        return '';
    }
  }

  setActiveTimesTab({ index }: { index: number; data: unknown }): void {
    this.activeTab$.next(index);
  }

  showModal(): void {
    this.isModalOpen = true;

    this.sendAnalytics('modal');
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.updateFilters$.next();
  }

  updateNextFiltersToStore(): void {
    this.showSpinner.emit();

    if (this.isNewFilterStyle) {
      const filters = this.filtersForm.getRawValue();

      const nextFilters = {
        departureTime: this.selectedNextDepartureTimes,
        arrivalTime: this.selectedNextArrivalTimes,
        maxStops: isPresent(filters?.stopsCount) ? Number(filters.stopsCount) : null,
        cabin: filters.cabin,
        refundable: filters.refundable === 'true' ? true : false,
      };

      this.setFilter.emit(nextFilters);
    }

    this.isModalOpen = false;
  }

  sendAnalytics(contextForAnalytics: string): void {
    this.gtmService.trackElement(
      `booking-flow-open-filters-${contextForAnalytics}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }

  onToggleDirectFlights(checked: boolean): void {
    this.showSpinner.emit();

    this.filtersForm.get('stopsCount').setValue(checked ? '0' : null);
    this.setFilter.emit({ maxStops: checked ? 0 : null });

    this.gtmService.trackElement(
      'booking-flow-direct-flights-shortcut-checkbox',
      GaContext.BOOKING_FLOW,
      ElementTypes.CHECKBOX,
      checked ? 'checked' : 'unchecked',
      ElementActions.CLICK
    );
  }

  private subscribeToTimesForm(
    form: FormGroup<Record<string, FormControl<boolean>>>,
    handler: (value: FlightTimeOption) => void
  ): Subscription {
    const timesFormSubscription = merge(
      ...Object.keys(FlightTimeOption).map((key) =>
        form.get(key).valueChanges.pipe(map(() => FlightTimeOption[key as keyof typeof FlightTimeOption]))
      )
    ).subscribe(handler);

    this.subscriptions.add(timesFormSubscription);

    return timesFormSubscription;
  }

  private buildTimesFormGroup(selectedTimes: FlightTimeOption[]): FormGroup<Record<string, FormControl<boolean>>> {
    const controls: Record<string, FormControl<boolean>> = {};
    for (const key of Object.keys(FlightTimeOption)) {
      controls[key] = new FormControl<boolean>(false);
    }
    const form = new FormGroup<Record<string, FormControl<boolean>>>(controls);
    this.syncTimesForm(form, selectedTimes);
    return form;
  }

  private syncTimesForm(
    form: FormGroup<Record<string, FormControl<boolean>>>,
    selectedTimes: FlightTimeOption[]
  ): void {
    if (!form) {
      return;
    }
    for (const key of Object.keys(FlightTimeOption)) {
      const value = FlightTimeOption[key as keyof typeof FlightTimeOption];
      form.get(key)?.setValue(selectedTimes?.includes(value) ?? true, { emitEvent: false });
    }
  }
}
